/**
 * Product Gallery Controller
 * Handles all gallery layout interactions:
 * - Carousel navigation (arrows, thumbnails)
 * - Touch/swipe support
 * - Video autoplay
 * - Keyboard navigation
 */

class ProductGallery {
  constructor(gallery) {
    this.gallery = gallery;
    this.layout = gallery.dataset.galleryLayout;
    this.slides = [];
    this.thumbnails = [];
    this.currentIndex = 0;
    this.isAnimating = false;
    
    this.init();
  }

  init() {
    switch (this.layout) {
      case 'carousel-thumbnails':
        this.initCarouselThumbnails();
        break;
      case 'grid-2-columns':
        this.initGrid();
        break;
      case 'vertical-scroll':
        this.initVerticalScroll();
        break;
    }
    
    this.initVideoAutoplay();
  }

  // ===== CAROUSEL WITH THUMBNAILS =====
  initCarouselThumbnails() {
    this.slides = Array.from(this.gallery.querySelectorAll('.product-gallery__slide'));
    this.thumbnails = Array.from(this.gallery.querySelectorAll('.product-gallery__thumbnail'));
    this.dots = Array.from(this.gallery.querySelectorAll('.product-gallery__dot'));
    this.counter = this.gallery.querySelector('.product-gallery__counter-current');
    this.prevBtn = this.gallery.querySelector('.product-gallery__nav--prev');
    this.nextBtn = this.gallery.querySelector('.product-gallery__nav--next');
    this.slidesContainer = this.gallery.querySelector('.product-gallery__slides');
    
    if (this.slides.length === 0) return;

    // Check if mobile
    const isMobile = window.matchMedia('(max-width: 767px)').matches;

    // Thumbnail interactions
    this.thumbnails.forEach((thumb, index) => {
      // Click always works (needed for mobile and accessibility)
      thumb.addEventListener('click', () => this.goToSlide(index));
      
      // Hover only on desktop
      if (!isMobile) {
        thumb.addEventListener('mouseenter', () => this.goToSlide(index));
      }
    });

    // Dots clicks
    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => this.goToSlide(index));
    });

    // Navigation arrows
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => this.prevSlide());
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => this.nextSlide());
    }

    // Touch/swipe support for tablet and mobile
    this.initSwipe();
    
    // Native scroll snap detection for mobile
    this.initScrollSnapDetection();

    // Keyboard navigation
    this.gallery.addEventListener('keydown', (e) => this.handleKeyboard(e));
    
    // Make gallery focusable
    this.gallery.setAttribute('tabindex', '0');
  }

  goToSlide(index) {
    if (this.isAnimating || index === this.currentIndex) return;
    if (index < 0) index = this.slides.length - 1;
    if (index >= this.slides.length) index = 0;

    this.isAnimating = true;

    // Update slides
    this.slides[this.currentIndex].classList.remove('is-active');
    this.slides[index].classList.add('is-active');

    // Scroll the slides container to show the target slide (needed for mobile scroll-snap)
    if (this.slidesContainer) {
      const targetSlide = this.slides[index];
      if (targetSlide) {
        this.slidesContainer.scrollTo({
          left: targetSlide.offsetLeft,
          behavior: 'smooth'
        });
      }
    }

    // Update thumbnails
    if (this.thumbnails.length > 0) {
      this.thumbnails[this.currentIndex].classList.remove('is-active');
      this.thumbnails[index].classList.add('is-active');
      
      // Scroll thumbnail into view
      this.thumbnails[index].scrollIntoView({ 
        behavior: 'smooth', 
        block: 'nearest' 
      });
    }

    // Update dots
    if (this.dots.length > 0) {
      this.dots[this.currentIndex].classList.remove('is-active');
      this.dots[index].classList.add('is-active');
    }

    // Update counter
    if (this.counter) {
      this.counter.textContent = index + 1;
    }

    // Handle video playback
    this.handleMediaChange(this.currentIndex, index);

    this.currentIndex = index;

    // Reset animation lock
    setTimeout(() => {
      this.isAnimating = false;
    }, 400);
  }

  nextSlide() {
    this.goToSlide(this.currentIndex + 1);
  }

  prevSlide() {
    this.goToSlide(this.currentIndex - 1);
  }

  // Touch/Swipe Support
  initSwipe() {
    const slidesContainer = this.gallery.querySelector('.product-gallery__slides') || 
                           this.gallery.querySelector('.product-gallery__main');
    if (!slidesContainer) return;

    let startX = 0;
    let startY = 0;
    let isDragging = false;
    const threshold = 50;

    slidesContainer.addEventListener('touchstart', (e) => {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      isDragging = true;
    }, { passive: true });

    slidesContainer.addEventListener('touchmove', (e) => {
      if (!isDragging) return;
      
      const diffX = e.touches[0].clientX - startX;
      const diffY = e.touches[0].clientY - startY;
      
      // Only prevent default if horizontal swipe
      if (Math.abs(diffX) > Math.abs(diffY)) {
        e.preventDefault();
      }
    }, { passive: false });

    slidesContainer.addEventListener('touchend', (e) => {
      if (!isDragging) return;
      isDragging = false;
      
      const endX = e.changedTouches[0].clientX;
      const diffX = endX - startX;

      if (Math.abs(diffX) > threshold) {
        if (diffX > 0) {
          this.prevSlide();
        } else {
          this.nextSlide();
        }
      }
    }, { passive: true });
  }

  // Scroll snap detection for mobile native scroll
  initScrollSnapDetection() {
    if (!this.slidesContainer) return;
    
    let scrollTimeout;
    this.slidesContainer.addEventListener('scroll', () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        // Calculate which slide is most visible
        const containerWidth = this.slidesContainer.offsetWidth;
        const scrollLeft = this.slidesContainer.scrollLeft;
        const newIndex = Math.round(scrollLeft / containerWidth);
        
        if (newIndex !== this.currentIndex && newIndex >= 0 && newIndex < this.slides.length) {
          this.updateSlideState(newIndex);
        }
      }, 100);
    }, { passive: true });
  }

  // Update slide state without animation (for scroll snap)
  updateSlideState(index) {
    // Update thumbnails
    if (this.thumbnails.length > 0) {
      this.thumbnails[this.currentIndex]?.classList.remove('is-active');
      this.thumbnails[index]?.classList.add('is-active');
    }

    // Update dots
    if (this.dots.length > 0) {
      this.dots[this.currentIndex]?.classList.remove('is-active');
      this.dots[index]?.classList.add('is-active');
    }

    // Update counter
    if (this.counter) {
      this.counter.textContent = index + 1;
    }

    // Handle video playback
    this.handleMediaChange(this.currentIndex, index);

    this.currentIndex = index;
  }

  // Keyboard Navigation
  handleKeyboard(e) {
    switch (e.key) {
      case 'ArrowLeft':
        e.preventDefault();
        this.prevSlide();
        break;
      case 'ArrowRight':
        e.preventDefault();
        this.nextSlide();
        break;
    }
  }

  // Handle video play/pause when changing slides
  handleMediaChange(oldIndex, newIndex) {
    // Pause video on old slide
    const oldVideo = this.slides[oldIndex]?.querySelector('video');
    if (oldVideo) {
      oldVideo.pause();
    }

    // Play video on new slide if autoplay enabled
    const newVideo = this.slides[newIndex]?.querySelector('video[data-autoplay-on-view]');
    if (newVideo) {
      newVideo.play().catch(() => {
        // Autoplay blocked, show play button
        const playBtn = newVideo.closest('.product-media__video-wrapper')
                               ?.querySelector('.product-media__video-play');
        if (playBtn) {
          playBtn.style.display = 'flex';
        }
      });
    }
  }

  // ===== GRID LAYOUT =====
  initGrid() {
    // Grid layout doesn't need JS interaction
    // Images are displayed in a static grid
    // Could add lightbox functionality here
  }

  // ===== VERTICAL SCROLL =====
  initVerticalScroll() {
    // Vertical layout doesn't need carousel JS
    // Images scroll naturally with page
    // Could add intersection observer for lazy loading
  }

  // ===== VIDEO AUTOPLAY =====
  initVideoAutoplay() {
    const videos = this.gallery.querySelectorAll('video[data-autoplay-on-view]');
    
    if (videos.length === 0) return;

    // Intersection Observer for autoplay
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        const video = entry.target;
        if (entry.isIntersecting) {
          video.play().catch(() => {
            // Show play button if autoplay fails
            const playBtn = video.closest('.product-media__video-wrapper')
                                 ?.querySelector('.product-media__video-play');
            if (playBtn) {
              playBtn.style.display = 'flex';
            }
          });
        } else {
          video.pause();
        }
      });
    }, {
      threshold: 0.5
    });

    videos.forEach(video => observer.observe(video));

    // Play button click handlers
    this.gallery.querySelectorAll('.product-media__video-play').forEach(btn => {
      btn.addEventListener('click', () => {
        const wrapper = btn.closest('.product-media__video-wrapper');
        const video = wrapper?.querySelector('video');
        if (video) {
          video.play();
          btn.style.display = 'none';
        }
      });
    });
  }

  // ===== PUBLIC API =====
  goToMediaById(mediaId) {
    const index = this.slides.findIndex(slide => 
      slide.dataset.mediaId === String(mediaId)
    );
    if (index !== -1) {
      this.goToSlide(index);
    }
  }
}

// ===== PRODUCT COLOR SWATCH INTEGRATION =====
function initProductSwatchGallerySync() {
  const swatches = document.querySelectorAll('.main-product__swatch');
  const gallery = document.querySelector('.product-gallery');
  
  if (!swatches.length || !gallery) return;

  swatches.forEach(swatch => {
    swatch.addEventListener('click', () => {
      const mediaId = swatch.dataset.mediaId;
      if (mediaId && window.productGallery) {
        window.productGallery.goToMediaById(mediaId);
      }
    });
  });
}

// ===== ACCORDION FUNCTIONALITY =====
function initAccordions() {
  const accordions = document.querySelectorAll('.main-product__accordion-trigger');
  
  accordions.forEach(trigger => {
    trigger.addEventListener('click', () => {
      const isExpanded = trigger.getAttribute('aria-expanded') === 'true';
      const content = trigger.nextElementSibling;
      
      // Close all other accordions
      accordions.forEach(other => {
        if (other !== trigger) {
          other.setAttribute('aria-expanded', 'false');
          const otherContent = other.nextElementSibling;
          if (otherContent) {
            otherContent.setAttribute('aria-hidden', 'true');
          }
        }
      });
      
      // Toggle current accordion
      trigger.setAttribute('aria-expanded', !isExpanded);
      if (content) {
        content.setAttribute('aria-hidden', isExpanded);
      }
    });
  });
}

// ===== TABS FUNCTIONALITY =====
function initTabs() {
  const tabsContainer = document.querySelector('[data-product-tabs]');
  if (!tabsContainer) return;

  const buttons = tabsContainer.querySelectorAll('[data-tab-trigger]');
  const panels = tabsContainer.querySelectorAll('.main-product__tabs-panel');

  buttons.forEach(btn => {
    btn.addEventListener('click', () => {
      const targetId = btn.getAttribute('aria-controls');

      buttons.forEach(b => {
        b.classList.remove('is-active');
        b.setAttribute('aria-selected', 'false');
      });

      panels.forEach(p => {
        p.classList.remove('is-active');
        p.hidden = true;
      });

      btn.classList.add('is-active');
      btn.setAttribute('aria-selected', 'true');

      const targetPanel = document.getElementById(targetId);
      if (targetPanel) {
        targetPanel.classList.add('is-active');
        targetPanel.hidden = false;
      }
    });
  });
}

// ===== COMPLEMENTARY PRODUCTS =====
/**
 * Loads complementary products from Shopify Product Recommendations API
 * Integrates with Shopify Search & Discovery app
 * Hides section completely if no products are found
 */
function initComplementaryProducts() {
  const containers = document.querySelectorAll('[data-complementary-products]');
  
  containers.forEach(container => {
    const productId = container.dataset.productId;
    const sectionId = container.dataset.sectionId;
    const limit = parseInt(container.dataset.limit) || 4;
    const productsContainer = container.querySelector('[data-complementary-container]');
    const emptyMessage = container.querySelector('[data-complementary-empty]');
    const loadingEl = container.querySelector('.main-product__complementary-loading');
    
    if (!productId || !productsContainer) return;
    
    // Build URL with section_id for better recommendations
    const baseUrl = `${window.Shopify?.routes?.root || '/'}recommendations/products.json`;
    const params = new URLSearchParams({
      product_id: productId,
      limit: limit.toString(),
      intent: 'complementary'
    });
    
    // Add section_id if available (improves Search & Discovery integration)
    if (sectionId) {
      params.append('section_id', sectionId);
    }
    
    const url = `${baseUrl}?${params.toString()}`;
    
    fetch(url)
      .then(response => response.json())
      .then(data => {
        // Hide loading
        if (loadingEl) loadingEl.style.display = 'none';
        
        const products = data.products || [];
        
        if (products.length === 0) {
          // No products found - hide entire section
          container.style.display = 'none';
          return;
        }
        
        // Render products
        const html = products.map(product => renderComplementaryProduct(product, container)).join('');
        productsContainer.innerHTML = html;
        
        // Initialize add to cart buttons
        initComplementaryAddToCart(productsContainer);
      })
      .catch(error => {
        console.error('Error loading complementary products:', error);
        // On error, hide entire section
        container.style.display = 'none';
      });
  });
}

/**
 * Renders a single complementary product card
 */
function renderComplementaryProduct(product, container) {
  const root = container.closest('[data-complementary-products]');
  const addText = root?.dataset.addText || window.theme?.strings?.add || '';
  const soldOutText = root?.dataset.soldOutText || window.theme?.strings?.soldOut || '';

  const variant = product.variants?.[0];
  const price = variant?.price || product.price;
  const compareAtPrice = variant?.compare_at_price || product.compare_at_price;
  const isOnSale = compareAtPrice && parseFloat(compareAtPrice) > parseFloat(price);
  
  const formattedPrice = formatMoney(price);
  const formattedComparePrice = isOnSale ? formatMoney(compareAtPrice) : '';
  
  const image = product.featured_image || product.images?.[0] || '';
  const imageUrl = image ? getSizedImageUrl(image, '200x300') : '';
  
  return `
    <div class="complementary-product-card">
      <a href="${product.url}" class="complementary-product-card__image-link">
        ${imageUrl ? `<img src="${imageUrl}" alt="${escapeHtml(product.title)}" class="complementary-product-card__image" loading="lazy">` : '<div class="complementary-product-card__image"></div>'}
      </a>
      <div class="complementary-product-card__content">
        <div class="complementary-product-card__info">
          <a href="${product.url}" class="complementary-product-card__title-link">
            <h5 class="complementary-product-card__title">${escapeHtml(product.title)}</h5>
          </a>
          <p class="complementary-product-card__price ${isOnSale ? 'complementary-product-card__price--sale' : ''}">
            ${formattedPrice}
            ${isOnSale ? `<span class="complementary-product-card__price-compare">${formattedComparePrice}</span>` : ''}
          </p>
        </div>
        ${variant?.available !== false ? `
          <button type="button" 
                  class="complementary-product-card__add-btn" 
                  data-complementary-add
                  data-variant-id="${variant?.id || ''}">
            ${addText}
          </button>
        ` : `
          <button type="button" class="complementary-product-card__add-btn" disabled>
            ${soldOutText}
          </button>
        `}
      </div>
    </div>
  `;
}

/**
 * Initialize add to cart functionality for complementary products
 */
function initComplementaryAddToCart(container) {
  const buttons = container.querySelectorAll('[data-complementary-add]');
  
  buttons.forEach(btn => {
    btn.addEventListener('click', async (e) => {
      e.preventDefault();
      const variantId = btn.dataset.variantId;
      
      if (!variantId) return;
      
      const root = btn.closest('[data-complementary-products]');
      const addText = root?.dataset.addText || window.theme?.strings?.add || '';
      const addingText = root?.dataset.addingText || window.theme?.strings?.adding || '';
      const addedText = root?.dataset.addedText || window.theme?.strings?.added || '';
      const errorText = root?.dataset.errorText || window.theme?.strings?.error || '';

      btn.disabled = true;
      btn.textContent = addingText;
      
      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: [{ id: parseInt(variantId), quantity: 1 }]
          })
        });
        
        if (response.ok) {
          btn.textContent = addedText;
          
          // Dispatch cart update event
          document.dispatchEvent(new CustomEvent('cart:refresh'));
          
          // Reset button after delay
          setTimeout(() => {
            btn.textContent = addText;
            btn.disabled = false;
          }, 2000);
        } else {
          throw new Error('Failed to add to cart');
        }
      } catch (error) {
        console.error('Error adding to cart:', error);
        btn.textContent = errorText;
        setTimeout(() => {
          btn.textContent = addText;
          btn.disabled = false;
        }, 2000);
      }
    });
  });
}

/**
 * Format money using ThemeUtils or Shopify format fallback
 */
function formatMoney(cents) {
  if (window.ThemeUtils?.formatMoney) {
    return window.ThemeUtils.formatMoney(cents);
  }
  if (typeof cents === 'string') {
    cents = parseFloat(cents) * 100;
  }
  const format = window.Shopify?.money_format || '${{amount}}';
  const amount = (cents / 100).toFixed(2);
  return format.replace(/\{\{\s*amount\s*\}\}/, amount)
               .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.floor(cents / 100))
               .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, amount.replace('.', ','));
}

/**
 * Get sized image URL from Shopify CDN
 */
function getSizedImageUrl(src, size) {
  if (!src) return '';
  
  // Handle Shopify CDN URLs
  if (src.includes('cdn.shopify.com')) {
    return src.replace(/(_\d+x\d+)?\.(\w+)(\?.*)?$/, `_${size}.$2$3`);
  }
  
  return src;
}

/**
 * Escape HTML to prevent XSS
 */
function escapeHtml(str) {
  const div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
}

// ===== INITIALIZATION =====
document.addEventListener('DOMContentLoaded', () => {
  // Initialize gallery
  const gallery = document.querySelector('.product-gallery');
  if (gallery) {
    window.productGallery = new ProductGallery(gallery);

    // If product.js already fired a mediaChange before gallery was ready, apply it now
    if (window._pendingMediaId) {
      window.productGallery.goToMediaById(window._pendingMediaId);
      window._pendingMediaId = null;
    }
  }

  // Listen for variant media change events from product.js
  document.addEventListener('product:mediaChange', (e) => {
    const mediaId = e.detail?.mediaId;
    if (mediaId && window.productGallery) {
      window.productGallery.goToMediaById(mediaId);
    }
  });

  // Initialize swatch-gallery sync
  initProductSwatchGallerySync();

  // Initialize accordions
  initAccordions();

  // Initialize tabs (alternate accordion display)
  initTabs();
  
  // Initialize complementary products
  initComplementaryProducts();
});

// Export for external use
if (typeof module !== 'undefined' && module.exports) {
  module.exports = ProductGallery;
}
